import { Engine } from "@babylonjs/core";
import { AudioManager } from "./AudioManager";
import { ProgressManager, sanitizeGamePreferences, type GamePreferences } from "../gameplay/ProgressManager";
import { JungleScene } from "../scenes/JungleScene";
import { VictoryScene } from "../scenes/VictoryScene";
import { MainMenu } from "../ui/MainMenu";
import { CreditsScreen } from "../ui/CreditsScreen";
import { LoadingScreen } from "../ui/LoadingScreen";
import { PwaManager } from "../pwa/PwaManager";
import { DifficultyManager, type DifficultyId } from "../gameplay/Difficulty";

export class Game {
  private readonly engine: Engine;
  private readonly audio = new AudioManager();
  private readonly progress = new ProgressManager();
  private readonly pwa = new PwaManager();
  private readonly difficulty: DifficultyManager;
  private preferences: GamePreferences;
  private jungle?: JungleScene;
  private victory?:VictoryScene;
  private loading?: LoadingScreen;
  private loadGeneration = 0;
  private menu?: MainMenu;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly uiRoot: HTMLElement) {
    this.difficulty = new DifficultyManager(this.progress.load().difficultyId);
    this.preferences = this.progress.loadPreferences();
    this.audio.setPreferences(this.preferences.masterVolume, this.preferences.muted, this.preferences.ambienceVolume, this.preferences.sfxVolume);
    this.engine = new Engine(canvas, true, { preserveDrawingBuffer: false, stencil: false, powerPreference: "high-performance" }, true);
    this.applyHardwareScaling();
    this.engine.runRenderLoop(() => {this.jungle?.render();this.victory?.render();});
    window.addEventListener("resize", () => this.engine.resize());
    window.addEventListener("ph:menu", () => this.showMenu());
    this.pwa.onInstallAvailability(()=>this.menu?.updateInstallExperience());
    this.pwa.start();
  }

  start(): void { this.showMenu(); }

  private showMenu(openDifficultySelection=false): void {
    this.loadGeneration++;this.loading?.dispose();this.loading=undefined;this.jungle?.dispose(); this.jungle = undefined;this.victory?.dispose();this.victory=undefined; this.uiRoot.innerHTML = ""; this.canvas.classList.remove("active");
    this.menu=new MainMenu(this.uiRoot, this.progress.load(), this.preferences, {
      onStart: (difficultyId) => this.startNewCampaign(difficultyId),
      onPreferences: (preferences) => { this.preferences = preferences; this.progress.savePreferences(preferences); this.audio.setPreferences(preferences.masterVolume, preferences.muted, preferences.ambienceVolume, preferences.sfxVolume); this.applyHardwareScaling(); },
      onCredits: () => new CreditsScreen(this.uiRoot, () => this.showMenu()).show(),
      onInstall: () => { void this.pwa.promptInstall().finally(()=>this.menu?.updateInstallExperience()); },
    },this.pwa);this.menu.show();if(openDifficultySelection)this.menu.showDifficultySelection();
  }

  private startNewCampaign(difficultyId:DifficultyId):void {
    this.difficulty.initializeNewCampaign(difficultyId,(id)=>{this.progress.resetCampaign(id);});
    this.startLevel();
  }

  private startLevel(): void {
    const generation=++this.loadGeneration;this.loading?.dispose();this.jungle?.dispose();this.jungle=undefined;this.victory?.dispose();this.victory=undefined;this.canvas.classList.remove("active");void this.audio.unlock();
    const loading=new LoadingScreen(this.uiRoot,{onEnter:()=>{if(generation!==this.loadGeneration||!this.jungle)return;this.loading?.dispose();this.loading=undefined;this.canvas.classList.add("active");this.jungle.activate();},onRetry:()=>this.startLevel(),onCancel:()=>this.showMenu()});
    this.loading=loading;loading.show();
    try{
      const campaign=this.progress.load();this.difficulty.select(campaign.difficultyId);
      const jungle=new JungleScene(this.engine,this.canvas,this.uiRoot,this.audio,this.progress,this.preferences,this.difficulty.current,{onMenu:()=>this.showMenu(),onDifficultyRestart:(difficultyId)=>this.startNewCampaign(difficultyId),onVictory:()=>queueMicrotask(()=>this.showVictory()),onPreferences:(preferences)=>{this.preferences=sanitizeGamePreferences(preferences);this.audio.setPreferences(this.preferences.masterVolume,this.preferences.muted,this.preferences.ambienceVolume,this.preferences.sfxVolume);this.applyHardwareScaling();},onLoading:(update)=>{if(generation===this.loadGeneration)loading.update(update);}});this.jungle=jungle;
      void jungle.ready.then(()=>{if(generation===this.loadGeneration&&this.jungle===jungle)loading.markReady();}).catch((error:unknown)=>{console.error("Gagal menyiapkan hutan",error);if(generation===this.loadGeneration)loading.showError(this.loadingErrorMessage());});
    }catch(error){console.error("Gagal membina hutan",error);if(generation===this.loadGeneration)loading.showError(this.loadingErrorMessage());}
  }

  private showVictory():void{this.loadGeneration++;this.loading?.dispose();this.loading=undefined;this.jungle?.dispose();this.jungle=undefined;this.victory?.dispose();this.audio.stopAmbience(1.2);this.uiRoot.innerHTML="";const victory=new VictoryScene(this.engine,this.canvas,this.uiRoot,this.progress.load(),{onReplay:()=>this.showMenu(true),onMenu:()=>this.showMenu()});this.victory=victory;victory.activate();void victory.ready.catch(error=>console.warn("Koleksi hantu tidak dapat dimuat sepenuhnya.",error));}

  private loadingErrorMessage():string { return navigator.onLine?"Hutan gagal disiapkan. Cuba sekali lagi.":"Sambungan internet diperlukan untuk memuatkan fail permainan yang belum disimpan."; }

  private applyHardwareScaling(): void {
    const mobile = matchMedia("(pointer: coarse)").matches;
    const scale = this.preferences.quality === "low" ? (mobile ? 1.8 : 1.35) : this.preferences.quality === "medium" ? (mobile ? 1.35 : 1) : 1;
    this.engine.setHardwareScalingLevel(scale);
  }
}
